import { globalObservability } from '@opendoor/observability/slim';
import Cookies from 'js-cookie';
import { z } from 'zod';

import type {
  SellerApplicationOwnerRelationship,
  SellerApplicationStartInput,
  SellerApplicationTrackingEventInput,
  SellerDenialReason,
  SellerLeadChannel,
} from '../../__generated__/zeus/types';
import { corePdpLink } from '../helpers/links';
import { getAnonymousId } from '../shared/trackers';
import {
  ConfirmSellerAddressEntryDocument,
  SellerAddressListingRouteDocument,
  SellerAddressRequiresUnitSelectionDocument,
  StandardizeSellerAddressDocument,
  StartSellerApplicationDocument,
} from './__generated__/seller-address-intake.zeus';
import type { StandardizeSellerAddressQuery } from './__generated__/seller-address-intake.zeus';
import type { IAddressInputAddress } from './addressTypes';
import { redactGraphqlRequestErrorForTelemetry } from './graphqlRequestError';
import { requestZeusFromBrowser, requestZeusReadFromBrowser } from './zeusBrowserClient';

const Sentry = globalObservability.getSentryClient();
const uuidSchema = z.uuid();

const SELLER_LEAD_CHANNELS = {
  AGENT_REQUEST: 'AGENT_REQUEST',
  AGENT_REQUEST_LISTED: 'AGENT_REQUEST_LISTED',
  APP: 'APP',
  BUY_SELL_FLOW: 'BUY_SELL_FLOW',
  CAKE_LANDING_PAGE: 'CAKE_LANDING_PAGE',
  CCPA_OPT_OUT: 'CCPA_OPT_OUT',
  DEFAULT: 'DEFAULT',
  EMAIL_PROSPECTING: 'EMAIL_PROSPECTING',
  EXCLUSIVE_OFFERS: 'EXCLUSIVE_OFFERS',
  FLYWHEEL: 'FLYWHEEL',
  HOME_RESERVE: 'HOME_RESERVE',
  HOME_VALUE: 'HOME_VALUE',
  OPENDOOR_BACKED_OFFER: 'OPENDOOR_BACKED_OFFER',
  OUTBOUND_OFFERS_LISTED: 'OUTBOUND_OFFERS_LISTED',
  OUTBOUND_OFFERS_UNDERWRITTEN: 'OUTBOUND_OFFERS_UNDERWRITTEN',
  PARTNER_API: 'PARTNER_API',
  REIT_BULK_REQUEST: 'REIT_BULK_REQUEST',
  REIT_BULK_REQUEST_LISTED: 'REIT_BULK_REQUEST_LISTED',
  SELLER_REQUEST_LISTED: 'SELLER_REQUEST_LISTED',
  TRADE_IN: 'TRADE_IN',
} as const satisfies Record<SellerLeadChannel, SellerLeadChannel>;

const sellerLeadChannelSchema = z.enum(SELLER_LEAD_CHANNELS);
const trackingCookieSchema = z.record(
  z.string(),
  z.array(z.string().catch('')).nullable().catch(null),
);

const MAX_TRACKING_EVENTS = 50;
const MAX_TRACKING_KEYWORD_LENGTH = 200;
const MAX_ADDRESS_ENTRY_URL_LENGTH = 2048;
const MAX_OPTIONAL_METADATA_LENGTH = 200;
const LEGACY_TRACKING_TIMESTAMP =
  /^(\d{4}-\d{2}-\d{2})[ +](\d{2}:\d{2}:\d{2})[ +]([+-]\d{2})(\d{2})$/u;
const ISO_TRACKING_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/u;

let fallbackAnonymousId: string | undefined;

export type StandardizedSellerAddress = NonNullable<
  StandardizeSellerAddressQuery['address']['standardize']
>;

export type SellerAddressIntakeErrorKind =
  | 'generic-error'
  | 'invalid-address'
  | 'postal-code-mismatch';

export class SellerAddressIntakeError extends Error {
  constructor(
    readonly kind: SellerAddressIntakeErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'SellerAddressIntakeError';
  }
}

const createAnonymousId = (): string => {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const validUuid = (value: string | undefined): string | undefined => {
  const normalized = value?.trim().replace(/^"|"$/gu, '');
  return normalized && uuidSchema.safeParse(normalized).success ? normalized : undefined;
};

const validCookieUuid = (name: string): string | undefined => validUuid(Cookies.get(name));

const zeusAnonymousId = (): string => {
  const cookieId = validUuid(getAnonymousId());
  if (cookieId) {
    return cookieId;
  }
  fallbackAnonymousId ??= createAnonymousId();
  return fallbackAnonymousId;
};

const parseTrackingTimestamp = (value: string): Date | undefined => {
  const legacy = LEGACY_TRACKING_TIMESTAMP.exec(value);
  let normalized: string | undefined;
  if (legacy) {
    normalized = `${legacy[1]}T${legacy[2]}${legacy[3]}:${legacy[4]}`;
  } else if (ISO_TRACKING_TIMESTAMP.test(value)) {
    normalized = value;
  }
  if (!normalized) return undefined;

  const trackedAt = new Date(normalized);
  return Number.isNaN(trackedAt.getTime()) ? undefined : trackedAt;
};

const trackingEventsFromCookie = (): SellerApplicationTrackingEventInput[] => {
  const value = Cookies.get('tracking');
  if (!value) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    Sentry.captureException?.(new Error('Invalid seller address tracking cookie'));
    return [];
  }

  const cookie = trackingCookieSchema.safeParse(parsed);
  if (!cookie.success) return [];

  let latest: { keywords: string[]; trackedAt: Date } | undefined;
  for (const [timestamp, keywords] of Object.entries(cookie.data)) {
    if (keywords === null) continue;
    const trackedAt = parseTrackingTimestamp(timestamp);
    if (!trackedAt) continue;
    if (!latest || trackedAt > latest.trackedAt) latest = { keywords, trackedAt };
  }
  if (!latest) return [];

  const events: SellerApplicationTrackingEventInput[] = [];
  for (const rawKeyword of latest.keywords) {
    const keyword = rawKeyword.trim();
    if (keyword.length === 0 || keyword.length > MAX_TRACKING_KEYWORD_LENGTH) continue;
    events.push({ keyword, trackedAt: latest.trackedAt.toISOString() });
    if (events.length === MAX_TRACKING_EVENTS) break;
  }
  return events;
};

const addressEntryUrl = (): string => {
  const url = new URL(window.location.href);
  if (url.href.length <= MAX_ADDRESS_ENTRY_URL_LENGTH) return url.href;
  url.hash = '';
  if (url.href.length <= MAX_ADDRESS_ENTRY_URL_LENGTH) return url.href;
  url.search = '';
  return url.href.length <= MAX_ADDRESS_ENTRY_URL_LENGTH ? url.href : `${url.origin}/`;
};

export const parseSellerLeadChannel = (value?: string): SellerLeadChannel | undefined =>
  value ? sellerLeadChannelSchema.safeParse(value.trim().toUpperCase()).data : undefined;

const optionalTrimmed = (value: string | undefined, maxLength?: number): string | undefined => {
  const trimmed = value?.trim();
  return trimmed && (maxLength === undefined || trimmed.length <= maxLength) ? trimmed : undefined;
};

export const standardizeSellerAddress = async (
  address: IAddressInputAddress,
): Promise<StandardizedSellerAddress | null> => {
  const unitNumber = optionalTrimmed(address.unit);
  const result = await requestZeusReadFromBrowser(StandardizeSellerAddressDocument, {
    input: {
      city: address.city.trim(),
      postalCode: address.postal_code.trim(),
      state: address.state.trim(),
      street: address.street1.trim(),
      ...(unitNumber ? { unitNumber } : {}),
    },
  });
  return result.address.standardize;
};

export const sellerAddressRequiresUnitSelection = async (addressId: string): Promise<boolean> => {
  const result = await requestZeusReadFromBrowser(SellerAddressRequiresUnitSelectionDocument, {
    addressId,
  });
  return result.address.requiresUnitSelection;
};

const sellerApplicationAddress = ({
  address,
  addressId,
  userConfirmed,
}: {
  address: IAddressInputAddress;
  addressId?: string;
  userConfirmed?: boolean;
}): SellerApplicationStartInput['address'] => {
  if (addressId && !userConfirmed) {
    return { existingAddress: { addressId } };
  }

  const unit = optionalTrimmed(address.unit);
  return {
    manualAddress: {
      city: address.city.trim(),
      postalCode: address.postal_code.trim(),
      state: address.state.trim(),
      street: address.street1.trim(),
      ...(unit ? { unit } : {}),
      ...(userConfirmed ? { userConfirmed: true } : {}),
    },
  };
};

const sellerApplicationPdpUrl = async (
  addressId: string,
  denialReasons: readonly SellerDenialReason[],
): Promise<string | undefined> => {
  if (
    !denialReasons.some(
      (reason) => reason === 'OWNED_BY_OPENDOOR' || reason === 'LISTED_BY_OPENDOOR',
    )
  ) {
    return undefined;
  }
  try {
    const result = await requestZeusReadFromBrowser(SellerAddressListingRouteDocument, {
      addressId,
    });
    const summary = result.marketplace.listingDetails?.summary;
    return summary?.slug ? corePdpLink(summary.slug, summary.addressId) : undefined;
  } catch (error) {
    Sentry.captureException?.(redactGraphqlRequestErrorForTelemetry(error));
    // A PDP redirect is optional; a completed seller application can continue onboarding without it.
    return undefined;
  }
};

const unexpectedSellerAddressIntakeResult = (_result: never): never => {
  throw new SellerAddressIntakeError('generic-error', 'Unexpected seller address response');
};

export const startSellerApplication = async ({
  address,
  addressId,
  channel,
  covid19Whitelist,
  flowId,
  flowName,
  partnerReferralId,
  userConfirmed,
}: {
  address: IAddressInputAddress;
  addressId?: string;
  channel?: SellerLeadChannel;
  covid19Whitelist?: boolean;
  flowId?: string;
  flowName?: string;
  partnerReferralId?: string;
  userConfirmed?: boolean;
}): Promise<{ applicationId: string; pdpUrl?: string; postalCode: string }> => {
  const parsedAddressId = validUuid(addressId);
  const parsedFlowId = validUuid(flowId);
  const parsedFlowName = optionalTrimmed(flowName, MAX_OPTIONAL_METADATA_LENGTH);
  const parsedPartnerReferralId = optionalTrimmed(partnerReferralId, MAX_OPTIONAL_METADATA_LENGTH);
  const previousApplicationId = validCookieUuid('sellerInputUUID');
  const input: SellerApplicationStartInput = {
    address: sellerApplicationAddress({ address, addressId: parsedAddressId, userConfirmed }),
    addressEntry: { product: 'SELL_DIRECT', url: addressEntryUrl() },
    attribution: {
      anonymousId: zeusAnonymousId(),
      ...(previousApplicationId ? { previousApplicationId } : {}),
      trackingEvents: trackingEventsFromCookie(),
    },
    ...(channel ? { channel } : {}),
    ...(covid19Whitelist ? { covid19Whitelist: true } : {}),
    ...(parsedFlowId ? { flowId: parsedFlowId } : {}),
    ...(parsedFlowName ? { flowName: parsedFlowName } : {}),
    ...(parsedPartnerReferralId ? { partnerReferralId: parsedPartnerReferralId } : {}),
  };

  const response = await requestZeusFromBrowser(StartSellerApplicationDocument, { input });
  const result = response.sellerApplicationStart;
  switch (result.__typename) {
    case 'SellerApplication': {
      if (!result.address) {
        throw new SellerAddressIntakeError('generic-error', 'Seller application has no address');
      }
      return {
        applicationId: result.id,
        postalCode: result.address.postalCode,
        pdpUrl: await sellerApplicationPdpUrl(
          result.address.id,
          result.productOfferings.map(({ denialReason }) => denialReason),
        ),
      };
    }
    case 'InvalidInputError':
      throw new SellerAddressIntakeError(
        'invalid-address',
        'Seller application address is invalid',
      );
    case 'NotFoundError':
    case 'UnauthorizedError':
      throw new SellerAddressIntakeError('generic-error', 'Seller application creation failed');
    default:
      return unexpectedSellerAddressIntakeResult(result);
  }
};

const OWNER_RELATIONSHIP_BY_ANSWER = {
  Agent: 'AGENT',
  AgentAndOwner: 'AGENT_AND_OWNER',
  Self: 'SELF',
  other: 'OTHER',
} as const satisfies Record<string, SellerApplicationOwnerRelationship>;

export type SellerOwnerRelationshipAnswer = keyof typeof OWNER_RELATIONSHIP_BY_ANSWER;

export const confirmSellerAddressEntry = async (
  applicationId: string,
  relationship?: SellerOwnerRelationshipAnswer,
): Promise<void> => {
  const response = await requestZeusFromBrowser(ConfirmSellerAddressEntryDocument, {
    input: {
      applicationId,
      step: {
        addressEntryConfirmation: relationship
          ? { ownerRelationship: OWNER_RELATIONSHIP_BY_ANSWER[relationship] }
          : {},
      },
    },
  });
  const result = response.sellerApplicationStepUpdate;
  switch (result.__typename) {
    case 'SellerApplication':
      return;
    case 'InvalidInputError':
    case 'NotFoundError':
    case 'UnauthorizedError':
      throw new SellerAddressIntakeError('generic-error', 'Seller address confirmation failed');
    default:
      return unexpectedSellerAddressIntakeResult(result);
  }
};
